import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ImpersonationBanner } from '@/components/ImpersonationBanner';

// Module accent map — preserves each module's identity inside the cobalt frame
const MODULE_ACCENTS = {
  sales:   { glow: 'rgba(99,102,241,0.55)',  rgb: '99,102,241',  fg: '#A5B4FC', name: 'Indigo'   },
  leads:   { glow: 'rgba(16,185,129,0.55)',  rgb: '16,185,129',  fg: '#6EE7B7', name: 'Emerald'  },
  service: { glow: 'rgba(56,189,248,0.55)',  rgb: '56,189,248',  fg: '#7DD3FC', name: 'Sky'      },
  spares:  { glow: 'rgba(212,165,116,0.55)', rgb: '212,165,116', fg: '#F4C067', name: 'Champagne'},
};

// Cobalt & Platinum themed Module Card
const ModuleCard = ({ id, title, subtitle, icon, onClick, isAvailable = true }) => {
  const accent = MODULE_ACCENTS[id] || MODULE_ACCENTS.sales;
  return (
    <div
      onClick={isAvailable ? onClick : undefined}
      className={`relative group cursor-pointer transition-all duration-500 ease-out hover:-translate-y-1 ${!isAvailable ? 'opacity-55 cursor-not-allowed' : ''}`}
      data-testid={`module-card-${id}`}
    >
      {/* Module-tinted edge glow on hover */}
      <div
        className="absolute -inset-1 rounded-2xl blur-2xl opacity-0 group-hover:opacity-90 transition-opacity duration-500 ease-out pointer-events-none"
        style={{ background: `radial-gradient(50rem 14rem at 50% 0%, ${accent.glow} 0%, transparent 65%)` }}
      ></div>

      {/* Cobalt glass card with platinum border */}
      <div
        className="relative rounded-2xl p-8 h-full transition-all duration-500 ease-out backdrop-blur-xl overflow-hidden"
        style={{
          background:
            'linear-gradient(160deg, rgba(15,33,68,0.78) 0%, rgba(11,29,58,0.85) 60%, rgba(7,18,40,0.92) 100%)',
          border: '1px solid rgba(229,231,235,0.18)',
          boxShadow:
            '0 1px 0 rgba(255,255,255,0.06) inset, 0 30px 60px -30px rgba(0,0,0,0.55), 0 8px 24px -10px rgba(11,29,58,0.45)',
        }}
      >
        {/* Brushed-metal sheen along top edge */}
        <div
          className="absolute inset-x-0 top-0 h-px pointer-events-none"
          style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(229,231,235,0.55) 50%, transparent 100%)' }}
        ></div>

        {/* Subtle platinum dust */}
        <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none opacity-40">
          <div className="absolute w-1.5 h-1.5 rounded-full top-5 left-8 bg-slate-200/60"></div>
          <div className="absolute w-1 h-1 rounded-full top-12 right-12 bg-slate-200/40"></div>
          <div className="absolute w-1 h-1 rounded-full bottom-10 left-16 bg-slate-200/50"></div>
        </div>

        {/* Icon tile — platinum surface with module emoji */}
        <div
          className="relative w-20 h-20 rounded-2xl flex items-center justify-center mb-6 transition-transform duration-500 ease-out group-hover:scale-[1.03] group-hover:rotate-2"
          style={{
            background:
              'linear-gradient(135deg, #E8EAEF 0%, #F8F9FB 35%, #C9CDD6 100%)',
            boxShadow:
              `0 1px 0 rgba(255,255,255,0.85) inset, 0 0 0 1px rgba(255,255,255,0.18) inset, 0 12px 28px -10px ${accent.glow}, 0 4px 12px rgba(11,29,58,0.35)`,
          }}
        >
          <span className="text-4xl drop-shadow-sm">{icon}</span>
        </div>

        {/* Content — Geist tracking-tight headlines on platinum tones */}
        <h3
          className="text-2xl font-semibold mb-2 tracking-tight transition-colors duration-300"
          style={{ fontFamily: 'Geist, Inter, sans-serif', color: '#F1F5F9' }}
        >
          {title}
        </h3>
        <p className="text-slate-300/85 text-sm leading-relaxed font-light">{subtitle}</p>

        {/* Arrow — appears on hover with module accent */}
        <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 translate-x-2 group-hover:translate-x-0 transition-all duration-300 ease-out">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: accent.fg }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </div>

        {/* Coming Soon Badge — platinum chip on cobalt */}
        {!isAvailable && (
          <div
            className="absolute top-4 right-4 px-3 py-1 rounded-full backdrop-blur-md"
            style={{
              background: 'linear-gradient(135deg, rgba(229,231,235,0.18) 0%, rgba(229,231,235,0.06) 100%)',
              border: '1px solid rgba(229,231,235,0.25)',
            }}
          >
            <span className="text-[11px] font-medium tracking-wider uppercase" style={{ color: '#E5E7EB' }}>Coming Soon</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Cobalt-glass nav chip
const NavChip = ({ onClick, accentRgb, icon, label, testid }) => (
  <button
    onClick={onClick}
    className="relative px-3.5 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition-all duration-240 ease-out hover:-translate-y-0.5 active:scale-95 backdrop-blur-md"
    style={{
      background: `linear-gradient(135deg, rgba(${accentRgb},0.16) 0%, rgba(${accentRgb},0.06) 100%)`,
      border: '1px solid rgba(229,231,235,0.20)',
      color: '#F1F5F9',
      boxShadow: '0 1px 0 rgba(255,255,255,0.06) inset, 0 4px 12px rgba(11,29,58,0.25)',
    }}
    data-testid={testid}
  >
    {icon}
    {label}
  </button>
);

function ModuleSelection({ user, onLogout }) {
  const navigate = useNavigate();

  const enabledModules = React.useMemo(() => {
    try {
      if (user?.role_name === 'Service Technician' || user?.role_name === 'Supervisor') {
        return ['service'];
      }
      const stored = localStorage.getItem('enabled_modules');
      if (stored) {
        return JSON.parse(stored);
      }
      if (user?.is_super_admin) {
        return ['lead_management', 'sales', 'service', 'spares'];
      }
      return ['lead_management', 'sales'];
    } catch {
      return ['lead_management', 'sales'];
    }
  }, [user]);

  React.useEffect(() => {
    if (user?.role_name === 'Service Technician' || user?.role_name === 'Supervisor') {
      navigate('/service/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const modules = [
    {
      id: 'sales',
      title: 'Sales',
      subtitle: 'Complete vehicle booking management, customer records, reports, and analytics dashboard',
      icon: '🚗',
      path: '/sales/bookings',
      isAvailable: enabledModules.includes('sales'),
    },
    {
      id: 'leads',
      title: 'Lead Management',
      subtitle: 'Track prospects, manage sales pipeline, Kanban board, conversion analytics, and follow-up automation',
      icon: '🎯',
      path: '/leads/list',
      isAvailable: enabledModules.includes('lead_management'),
    },
    {
      id: 'spares',
      title: 'Spares',
      subtitle: 'Spare parts inventory, stock management, orders, and supply chain tracking',
      icon: '⚙️',
      path: '/spares',
      isAvailable: enabledModules.includes('spares'),
    },
    {
      id: 'service',
      title: 'Service',
      subtitle: 'Job card management, technician allocation, service tracking, and workshop operations',
      icon: '🔧',
      path: '/service/dashboard',
      isAvailable: enabledModules.includes('service'),
    },
  ];

  const sortedModules = [...modules].sort((a, b) => {
    if (a.isAvailable && !b.isAvailable) return -1;
    if (!a.isAvailable && b.isAvailable) return 1;
    return 0;
  });

  return (
    <div
      className="min-h-screen relative overflow-hidden anim-fade-in"
      style={{
        background:
          'radial-gradient(60rem 60rem at 80% -10%, rgba(30,58,138,0.55) 0%, transparent 60%),' +
          'radial-gradient(60rem 60rem at -10% 110%, rgba(15,33,68,0.85) 0%, transparent 65%),' +
          'linear-gradient(135deg, #050B1A 0%, #0B1D3A 50%, #06122A 100%)',
      }}
    >
      <ImpersonationBanner />

      {/* Platinum starfield grain */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              'radial-gradient(rgba(229,231,235,0.7) 0.6px, transparent 0.6px),' +
              'radial-gradient(rgba(229,231,235,0.45) 0.4px, transparent 0.4px)',
            backgroundSize: '46px 46px, 22px 22px',
            backgroundPosition: '0 0, 11px 11px',
          }}
        ></div>
        <div
          className="absolute -top-40 left-1/3 w-[28rem] h-[28rem] rounded-full opacity-30 blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.55) 0%, transparent 70%)' }}
        ></div>
        <div
          className="absolute bottom-[-8rem] right-1/4 w-[32rem] h-[32rem] rounded-full opacity-25 blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(56,189,248,0.45) 0%, transparent 70%)' }}
        ></div>
        <div
          className="absolute top-1/3 left-[-6rem] w-[22rem] h-[22rem] rounded-full opacity-20 blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(212,165,116,0.40) 0%, transparent 70%)' }}
        ></div>
      </div>

      {/* Header — cobalt glass with platinum hairline */}
      <header
        className="relative z-10 backdrop-blur-xl"
        style={{
          background:
            'linear-gradient(180deg, rgba(11,29,58,0.65) 0%, rgba(11,29,58,0.35) 100%)',
          borderBottom: '1px solid rgba(229,231,235,0.12)',
        }}
      >
        {/* platinum sheen line */}
        <div
          className="absolute inset-x-0 bottom-0 h-px pointer-events-none"
          style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(229,231,235,0.45) 50%, transparent 100%)' }}
        ></div>

        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Logo tile — platinum surface */}
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{
                  background:
                    'linear-gradient(135deg, #E8EAEF 0%, #F8F9FB 35%, #C9CDD6 100%)',
                  boxShadow:
                    '0 1px 0 rgba(255,255,255,0.85) inset, 0 0 0 1px rgba(255,255,255,0.18) inset, 0 8px 18px -6px rgba(11,29,58,0.55)',
                }}
              >
                <span className="text-2xl drop-shadow-sm">🚗</span>
              </div>
              <div>
                <h1
                  className="text-xl font-semibold tracking-tight"
                  style={{
                    fontFamily: 'Geist, Inter, sans-serif',
                    background: 'linear-gradient(135deg, #FFFFFF 0%, #E5E7EB 50%, #C9CDD6 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  AutoBook Pro
                </h1>
                <p className="text-[11px] tracking-[0.18em] uppercase font-medium" style={{ color: '#94A3B8' }}>
                  Automobile Management System
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2.5">
              {(user?.role_name === 'Company Admin' || user?.is_super_admin) && (
                <NavChip
                  onClick={() => navigate('/settings/users')}
                  accentRgb="167,139,250"
                  testid="user-management-btn"
                  icon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  }
                  label="Users"
                />
              )}
              {(user?.role_name === 'Company Admin' || user?.is_super_admin) && (
                <NavChip
                  onClick={() => navigate('/admin/database')}
                  accentRgb="56,189,248"
                  testid="database-admin-btn"
                  icon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                    </svg>
                  }
                  label="Database"
                />
              )}
              {(user?.role_name === 'Company Admin' || user?.is_super_admin) && (
                <NavChip
                  onClick={() => navigate('/settings/whatsapp')}
                  accentRgb="16,185,129"
                  testid="whatsapp-settings-btn"
                  icon={
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                  }
                  label="WhatsApp"
                />
              )}
              {(user?.role_name === 'Company Admin' || user?.is_super_admin) && (
                <NavChip
                  onClick={() => navigate(user?.is_super_admin ? '/admin/subscription-plans' : '/subscription')}
                  accentRgb="244,192,103"
                  testid="subscription-btn"
                  icon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                  }
                  label="Subscription"
                />
              )}
              <div className="text-right ml-1">
                <p className="text-sm font-medium" style={{ color: '#F1F5F9' }}>{user?.name || 'User'}</p>
                <p className="text-[11px]" style={{ color: '#94A3B8' }}>{user?.company_name || user?.email || 'user@example.com'}</p>
              </div>
              <button
                onClick={onLogout}
                className="px-3.5 py-2 rounded-xl text-sm font-medium transition-all duration-240 ease-out hover:-translate-y-0.5 active:scale-95 backdrop-blur-md"
                style={{
                  background: 'linear-gradient(135deg, rgba(244,63,94,0.16) 0%, rgba(244,63,94,0.06) 100%)',
                  border: '1px solid rgba(244,63,94,0.30)',
                  color: '#FCA5A5',
                  boxShadow: '0 1px 0 rgba(255,255,255,0.06) inset, 0 4px 12px rgba(11,29,58,0.25)',
                }}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-6 py-16 anim-slide-up">
        {/* Welcome Section */}
        <div className="text-center mb-16">
          <p className="text-[11px] font-semibold uppercase tracking-[0.32em] mb-5" style={{ color: '#94A3B8' }}>
            Command Center
          </p>
          <h2
            className="text-5xl md:text-6xl font-semibold mb-5 tracking-tight"
            style={{ fontFamily: 'Geist, Inter, sans-serif', color: '#F1F5F9' }}
          >
            Welcome to{' '}
            <span
              style={{
                background:
                  'linear-gradient(135deg, #FFFFFF 0%, #E5E7EB 35%, #C9CDD6 65%, #FFFFFF 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              AutoBook Pro
            </span>
          </h2>
          <p className="text-base md:text-lg max-w-2xl mx-auto font-light leading-relaxed" style={{ color: '#CBD5E1' }}>
            Your complete automobile management solution. Select a module to get started.
          </p>
        </div>

        {/* Module Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16 anim-grid-stagger">
          {sortedModules.map(module => (
            <ModuleCard
              key={module.id}
              id={module.id}
              title={module.title}
              subtitle={module.subtitle}
              icon={module.icon}
              isAvailable={module.isAvailable}
              onClick={() => navigate(module.path)}
            />
          ))}
        </div>

        {/* Quick Stats — cobalt glass with platinum numbers */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 anim-grid-stagger">
          {[
            { label: 'Total Bookings', value: '1,692', icon: '📋', accentRgb: '99,102,241'  },
            { label: 'This Month',     value: '156',   icon: '📈', accentRgb: '16,185,129'  },
            { label: 'Service Jobs',   value: '342',   icon: '🔧', accentRgb: '56,189,248'  },
            { label: 'Revenue',        value: '₹2.4Cr',icon: '💰', accentRgb: '244,192,103' },
          ].map((stat, idx) => (
            <div
              key={idx}
              className="relative rounded-2xl p-4 backdrop-blur-xl transition-all duration-300 ease-out hover:-translate-y-0.5"
              style={{
                background:
                  'linear-gradient(160deg, rgba(15,33,68,0.65) 0%, rgba(11,29,58,0.85) 100%)',
                border: '1px solid rgba(229,231,235,0.14)',
                boxShadow:
                  '0 1px 0 rgba(255,255,255,0.05) inset, 0 8px 24px -8px rgba(0,0,0,0.4)',
              }}
            >
              <div className="absolute inset-x-0 top-0 h-px pointer-events-none"
                style={{ background: `linear-gradient(90deg, transparent 0%, rgba(${stat.accentRgb},0.55) 50%, transparent 100%)` }}
              ></div>
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{
                    background: `linear-gradient(135deg, rgba(${stat.accentRgb},0.22) 0%, rgba(${stat.accentRgb},0.08) 100%)`,
                    border: `1px solid rgba(${stat.accentRgb},0.28)`,
                  }}
                >
                  <span className="text-lg">{stat.icon}</span>
                </div>
                <div>
                  <p
                    className="text-2xl font-semibold tracking-tight"
                    style={{
                      fontFamily: 'Geist, Inter, sans-serif',
                      background: 'linear-gradient(135deg, #FFFFFF 0%, #E5E7EB 60%, #C9CDD6 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    {stat.value}
                  </p>
                  <p className="text-[11px] uppercase tracking-[0.14em] font-medium" style={{ color: '#94A3B8' }}>{stat.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* Footer — platinum hairline */}
      <footer
        className="relative z-10 mt-auto"
        style={{ borderTop: '1px solid rgba(229,231,235,0.10)' }}
      >
        <div className="max-w-7xl mx-auto px-6 py-5">
          <p className="text-center text-[12px] tracking-wide font-light" style={{ color: '#94A3B8' }}>
            © 2026 AutoBook Pro · Crafted with care.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default ModuleSelection;
